import {
    PropsWithChildren,
    startTransition,
    useCallback,
    useMemo,
    useState,
} from "react";
import { InvoiceByCounterpartySortValue } from "@common/types/invoice";
import { useInvoicesByCounterpartyPageSortingCache } from "@hooks/view/useInvoicesByCounterpartyPageSortingCache";
import { useInvoicesByCounterparty } from "@api/invoice.api";
import {
    toInvoiceApiFilters,
    useInvoicesView,
} from "../../contexts/InvoicesViewContext";
import { useSortByCounterparty } from "./useSortByCounterparty";
import {
    InvoicesByCounterpartyTableContext,
    type InvoicesByCounterpartyTableContextValue,
} from "./InvoicesByCounterpartyTable.context";

const PER_PAGE = 50;

export const InvoicesByCounterpartyTableContextProvider: React.FC<
    PropsWithChildren
> = ({ children }) => {
    const [cachedSorting, setCachedSorting] =
        useInvoicesByCounterpartyPageSortingCache();
    const { filters, viewOption } = useInvoicesView();
    const [page, setPage] = useState(1);

    const { sortExpression, currentSort, handleSortChange } =
        useSortByCounterparty(cachedSorting);

    const { data, isLoading } = useInvoicesByCounterparty(
        {
            filters: toInvoiceApiFilters(filters),
            sort: sortExpression,
            page,
            limit: PER_PAGE,
        },
        { enabled: viewOption.groupBy === "counterparty" },
    );

    const handleSortChangeWithPageReset = useCallback(
        (value: InvoiceByCounterpartySortValue) => {
            startTransition(() => {
                handleSortChange(value);
                setCachedSorting(value);
                setPage(1);
            });
        },
        [handleSortChange, setCachedSorting, setPage],
    );

    const contextValue = useMemo<InvoicesByCounterpartyTableContextValue>(
        () => ({
            currentPage: page,
            totalPages: data?.pageCount ?? 1,
            counterpartiesCount: data?.total ?? 0,
            setPage,
            handleSortChange: handleSortChangeWithPageReset,
            currentSort,
            isLoading,
            data: data?.data,
        }),
        [page, data, handleSortChangeWithPageReset, currentSort, isLoading],
    );

    return (
        <InvoicesByCounterpartyTableContext.Provider value={contextValue}>
            {children}
        </InvoicesByCounterpartyTableContext.Provider>
    );
};
